/**
 * Guest Hub (kiosk) loading controller.
 *
 * The site-wide overlay in App.vue is driven by the boolean
 * `checkin-site/isLoading`, which the axios interceptors flip true on every
 * request and false on every response. That is fine for a page that makes one
 * call, and wrong for the kiosk: its screens boot with a dozen *sequential*
 * awaits, so the boolean drops to false for a few milliseconds between each
 * pair and the overlay blinks off over half-painted UI, then back on.
 *
 * This controller replaces the boolean with three things, and is bound only by
 * the kiosk branch of App.vue — the public check-in form keeps the overlay it
 * has always had.
 *
 *   1. A pending COUNT, so a Promise.all no longer hides the overlay when the
 *      first of several responses lands.
 *   2. HYSTERESIS. Hiding is deferred by a grace period, and any work that
 *      starts during it cancels the hide outright — that cancellation is what
 *      removes the flicker between sequential calls. A show is likewise
 *      deferred, so a request that answers in 80ms never flashes an overlay at
 *      all, and an overlay that does appear stays up long enough to be read
 *      rather than strobing.
 *   3. HOLDS, an explicit transaction boundary for a flow that is known to be
 *      several calls long (the kiosk form boot). A hold shows the overlay at
 *      once — there is nothing painted underneath yet to protect — and keeps
 *      it up across every gap in the flow, including calls made through
 *      `mrsApi`, until the caller releases it.
 *
 * WHAT IS SHOWN is not a question this controller answers: there is one
 * loading screen and every state here raises it — the same overlay the rest of
 * the product uses, whose text tells the guest not to go back or refresh the
 * page. The kiosk briefly had quieter states of its own, a bar and a ring, and
 * they are gone; a bar cannot say the one sentence that matters to somebody
 * standing at a shared tablet.
 *
 * What the states still separate is WHEN. `beginProcessing()` is the span of a
 * form being validated and submitted, and it is deliberately not measured the
 * way requests are: it has to cover the gap between a payment gateway
 * authorising a card and the write that follows, in which nothing at all is in
 * flight, and it has to survive a validation that fails inside the same tick
 * without ever painting.
 *
 * The kiosk's "Now Starting" panel is not in here at all: it belongs to the
 * start screen, which draws it over its own boot (see kiosk_home.vue).
 */

import Vue from 'vue';

// Wait this long before showing: work that finishes first never flashes.
const SHOW_DELAY_MS = 250;
// Keep the overlay up this long after the last response, so the next request
// in a sequence cancels the hide instead of letting the screen blink.
const HIDE_GRACE_MS = 300;
// Once shown, stay up at least this long — a 40ms overlay reads as a glitch.
const MIN_VISIBLE_MS = 400;
// A hold is released in a `finally`, but a kiosk runs unattended: if a caller
// is ever killed in a way that skips it, the overlay must not wedge the tablet.
const MAX_HOLD_MS = 30000;
// Processing covers validation as well as submission, and most of a form's
// validation is synchronous — a missing signature fails and returns inside the
// same tick. Shorter than the delay above, so a submit that does reach the
// network is covered almost at once, but long enough that a rejected field
// never flashes a full-screen overlay at a guest who is about to be shown an
// error message instead.
const PROCESSING_DELAY_MS = 200;

const state = Vue.observable({ visible: false, processing: false });

let pending = 0;
let holds = 0;
let silences = 0;
let show_timer = null;
let settle_timer = null;
let hold_timer = null;
let shown_at = 0;
// When the current run of work started, or 0 between runs. The show delay is
// measured against this rather than against each request, so a screen that
// makes six 120ms calls back to back counts as 720ms of work and shows the
// overlay — measured per request, none of them would ever reach the delay and
// the guest would watch the screen assemble itself in silence.
let episode_started_at = 0;

let processing = 0;
let processing_show_timer = null;
let processing_hide_timer = null;
let processing_shown_at = 0;

function busy() {
    if (silences > 0) return false;
    // A submit already has the screen. Counting requests towards a second
    // reason to show the same overlay would only make it flicker on the
    // handover between them.
    if (state.processing) return false;
    return pending > 0 || holds > 0;
}

function clearTimer(timer) {
    if (timer) clearTimeout(timer);
    return null;
}

function show() {
    shown_at = Date.now();
    state.visible = true;
}

// Called after every change to `pending`, `holds` or `silences`; decides what
// the overlay should be doing now, and when.
function sync() {
    if (busy()) {
        // The cancellation that removes the flicker: work started again before
        // the deferred hide fired, so the overlay simply never goes down.
        settle_timer = clearTimer(settle_timer);

        if (!episode_started_at) episode_started_at = Date.now();

        if (state.visible) return;

        if (holds > 0) {
            // A declared multi-call flow — no point waiting to see whether it
            // is quick, and on the kiosk boot there is a blank screen under it.
            show_timer = clearTimer(show_timer);
            return show();
        }

        const waited = Date.now() - episode_started_at;

        // Work resumed after the delay had already passed without anything in
        // flight to show for — this run has earned the overlay, so no more
        // waiting.
        if (waited >= SHOW_DELAY_MS) {
            show_timer = clearTimer(show_timer);
            return show();
        }

        if (show_timer) return;
        show_timer = setTimeout(() => {
            show_timer = null;
            // A gap at exactly this moment is not worth announcing; the next
            // request takes the branch above and shows immediately.
            if (busy()) show();
        }, SHOW_DELAY_MS - waited);

        return;
    }

    if (settle_timer) return;
    // Fully settled already — nothing pending to take down.
    if (!state.visible && !show_timer && !episode_started_at) return;

    // Nothing in flight. Wait out the grace period before believing it: this
    // is the window in which the next request in a sequence cancels the hide.
    const wait = state.visible
        ? Math.max(HIDE_GRACE_MS, MIN_VISIBLE_MS - (Date.now() - shown_at))
        : HIDE_GRACE_MS;

    settle_timer = setTimeout(() => {
        settle_timer = null;
        if (busy()) return sync();

        show_timer = clearTimer(show_timer);
        episode_started_at = 0;
        state.visible = false;
    }, wait);
}

// Processing keeps timers of its own rather than riding the ones above: it is
// not raised by requests, it is not deferred as long as they are, and it has to
// stay up across the gap between a validation call and the submit that follows
// it — a gap in which nothing at all is in flight.
function syncProcessing() {
    if (processing > 0) {
        processing_hide_timer = clearTimer(processing_hide_timer);
        if (state.processing || processing_show_timer) return;

        processing_show_timer = setTimeout(() => {
            processing_show_timer = null;
            if (processing === 0) return;

            processing_shown_at = Date.now();
            state.processing = true;
            // Take the quiet states down from under it.
            sync();
        }, PROCESSING_DELAY_MS);

        return;
    }

    processing_show_timer = clearTimer(processing_show_timer);
    if (!state.processing || processing_hide_timer) return;

    // Same reason as the overlay above: a submit that answers the moment the
    // overlay appears must not leave a single frame of it behind.
    const wait = Math.max(0, MIN_VISIBLE_MS - (Date.now() - processing_shown_at));

    processing_hide_timer = setTimeout(() => {
        processing_hide_timer = null;
        if (processing > 0) return syncProcessing();

        state.processing = false;
        // Whatever is still in flight underneath goes back to the quiet states.
        sync();
    }, wait);
}

export const loader = {
    // Bound by App.vue's kiosk branch.
    get visible() {
        return state.visible && !state.processing;
    },

    // A form is being validated or submitted. Raises the same overlay
    // `visible` does — the two are kept apart because they start and stop on
    // different rules, not because they look different.
    get processing() {
        return state.processing;
    },

    // One request started / settled. Called from the axios interceptors.
    begin() {
        pending += 1;
        sync();
    },

    end() {
        pending = Math.max(0, pending - 1);
        sync();
    },

    /**
     * Open a transaction: the overlay stays up until the matching release(),
     * whatever the requests underneath do. Balance it in a `finally`.
     */
    hold() {
        holds += 1;
        hold_timer = clearTimer(hold_timer);
        hold_timer = setTimeout(() => {
            hold_timer = null;
            // Safety net only — a balanced caller never reaches this.
            holds = 0;
            sync();
        }, MAX_HOLD_MS);
        sync();
    },

    release() {
        holds = Math.max(0, holds - 1);
        if (holds === 0) hold_timer = clearTimer(hold_timer);
        sync();
    },

    /**
     * A form submit is under way — from the first validation whose result the
     * guest cannot see, through to the response that carries them onwards.
     * Counted, so the span a submit method wraps around itself and the span the
     * processing modal opens underneath it are one overlay rather than two.
     * Balance in a `finally`, like hold().
     */
    beginProcessing() {
        processing += 1;
        syncProcessing();
    },

    endProcessing() {
        processing = Math.max(0, processing - 1);
        syncProcessing();
    },

    /**
     * Suppress the overlay for a screen that draws its own progress — the
     * kiosk start page, whose "Now Starting" panel already fills the tablet
     * while it boots. Requests keep being counted, they just do not raise
     * anything on top of what the screen is already saying. Balance in a
     * `finally`, like hold().
     */
    silence() {
        silences += 1;
        sync();
    },

    resume() {
        silences = Math.max(0, silences - 1);
        sync();
    },

    // Drop everything immediately. For a screen that resets the kiosk to the
    // start page and wants no overlay carried across.
    reset() {
        pending = 0;
        holds = 0;
        silences = 0;
        processing = 0;
        episode_started_at = 0;
        show_timer = clearTimer(show_timer);
        settle_timer = clearTimer(settle_timer);
        hold_timer = clearTimer(hold_timer);
        processing_show_timer = clearTimer(processing_show_timer);
        processing_hide_timer = clearTimer(processing_hide_timer);
        state.visible = false;
        state.processing = false;
    },
};

export default loader;
